import React, {useContext, useMemo} from 'react';
import {AttentionMessageProps} from './index';
import {UserContext} from '../../../../../src/contexts/UserContext';
import OrderAttentionControls, {makeOrderAttentionControlListener} from '../OrderAttentionControls';
import {useQueryClient} from '@tanstack/react-query';
import app from '../../../../../src/helpers/app';
import {refetchAllOrderQueriesExceptDisabled} from '../../../refetchAllOrderQueriesExceptDisabled';

export default function OrderDeletionRequest({setMessages, catchHttpException, ...props}: AttentionMessageProps) {
    const userContext = useContext(UserContext);
    const queryClient = useQueryClient();

    const modelId = props.model.id;
    const [onAccept, onReject] = useMemo(() => {
        const make = <Key extends 'onAccept' | 'onReject'>(routeName: string) => {
            return makeOrderAttentionControlListener<Key>(
                    routeName,
                    modelId,
                    () => refetchAllOrderQueriesExceptDisabled(
                            queryClient,
                            app('queryKeys').getAllQueriesOfExactModelQueryKey('Orders', modelId)
                    ),
                    catchHttpException,
                    undefined,
                    (message) => {
                        const permission = 'orders@can-solve-order-deletion-requests-without-comment';
                        if (!message?.length && userContext?.cant(permission)) {
                            setMessages(list => {
                                return [{
                                    color: 'danger',
                                    message: 'OrderAttentionsField.controls.comment-is-required'
                                }, ...list];
                            });

                            return Promise.reject();
                        }
                    }
            );
        };

        return [
            make<'onAccept'>('orders.accept-attention'),
            make<'onReject'>('orders.reject-attention'),
        ];
    }, [modelId, catchHttpException, queryClient, userContext, setMessages]);

    if (!userContext) {
        return null;
    }

    return <OrderAttentionControls
            model={props.model}
            acceptButtonColor="danger"
            rejectButtonColor="success"
            commentFieldPlaceholder={
                userContext.can('orders@can-solve-order-deletion-requests-without-comment') ?
                        'OrderAttentionsField.enter-comment' :
                        'OrderAttentionsField.comment-is-required'
            }
            onAccept={onAccept}
            onReject={onReject}
    />;
}
